<template>
  <div
    class="flex items-center justify-between gap-4"
   
  >
    <span
      v-if="label || description"
      class="flex grow flex-col"
    >
      <label
        v-if="label"
        :id="`${componentId}-label`"
        :class="computedLabelClasses"
      >
        {{ label }}
      </label>

      <span
        v-if="description"
        :id="`${componentId}-description`"
        :class="computedDescriptionClasses"
      >
        {{ description }}
      </span>
    </span>

    <div :class="computedRootClasses">
      <span :class="computedCircleClasses">
        <span
          v-if="hasIndicator"
          class="absolute inset-0 flex size-full items-center justify-center opacity-100 transition-opacity duration-200 ease-in group-has-checked:opacity-0 group-has-checked:duration-100 group-has-checked:ease-out"
          aria-hidden="true"
        >
          <svg
            :class="[
              'text-gray-400',
              computedIndicatorSizeClass,
            ]"
            fill="none"
            viewBox="0 0 12 12"
          >
            <path
              d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span
          v-if="hasIndicator"
          class="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-has-checked:opacity-100 group-has-checked:duration-200 group-has-checked:ease-in"
          aria-hidden="true"
        >
          <svg
            :class="[
              'text-secondary',
              computedIndicatorSizeClass,
            ]"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
          </svg>
        </span>
      </span>

      <input
        :id="componentId"
        v-bind="$attrs"
        v-model="model"
        type="checkbox"
        class="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer"
      >
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import type { ToggleSize } from './types'

/**
 * TODO:
 * - Add disabled state
 * - Add indicator state
 * - Add color variants
 */
const props = withDefaults(defineProps<{
  id?: string
  label?: string
  description?: string
  hasIndicator?: boolean
  size?: ToggleSize
}>(), {
  size: 'md',
})

const model = defineModel<T>()

defineOptions({
  inheritAttrs: false,
})

const componentId = computed(() => {
  return props.id ?? useId()
})

const computedLabelClasses = computed(() => {
  return [
    'font-medium text-gray-900',
    {
      'text-xs': props.size === 'xs',
      'text-sm': props.size === 'sm',
      'text-base': props.size === 'md',
      'text-lg': props.size === 'lg',
    },
  ]
})

const computedDescriptionClasses = computed(() => {
  return [
    'text-gray-500',
    {
      'text-xs/5': props.size === 'xs',
      'text-sm/5': props.size === 'sm',
      'text-sm': props.size === 'md',
      'text-base': props.size === 'lg',
    },
  ]
})

const computedRootClasses = computed(() => {
  return [
    'group relative inline-flex shrink-0 rounded-full bg-gray-200 p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-secondary transition-colors duration-200 ease-in-out has-checked:bg-secondary has-focus-visible:outline-2',
    {
      'w-9': props.size === 'xs',
      'w-11': props.size === 'sm',
      'w-13': props.size === 'md',
      'w-15': props.size === 'lg',
    },
  ]
})

const computedCircleClasses = computed(() => {
  return [
    'relative rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out',
    {
      'size-4 group-has-checked:translate-x-4': props.size === 'xs',
      'size-5 group-has-checked:translate-x-5': props.size === 'sm',
      'size-6 group-has-checked:translate-x-6': props.size === 'md',
      'size-7 group-has-checked:translate-x-7': props.size === 'lg',
    },
  ]
})

const computedIndicatorSizeClass = computed(() => {
  return {
    'size-3': props.size === 'xs',
    'size-3.5': props.size === 'sm',
    'size-4': props.size === 'md',
    'size-5': props.size === 'lg',
  }
})
</script>
